// src/pages/ChatBot/configs/config-rows.tsx
//
// The presentational row family for the Configs tab's detail pane, split out of
// ConfigsPanel so that file stays focused on orchestration:
//   ConfigRow        — one setting: label + chips + help + its control + the
//                      reset/undo affordance.
//   ControlField     — the input for a row, dispatched on the entry's kind /
//                      domain (variant select, number, boolean, enum, string,
//                      stringList). Internal to this module.
//   RerankToggleRow  — the presentation-only "Reranking enabled" toggle.
// All staged-edit judgement (displayValue / isCustom / …) lives in
// config-values; these components are pure props → markup. They use the cfg-*
// classes from ConfigsPanel.css, which the panel that mounts them imports.

import type { ConfigSurfaceEntry, RejectedOverride, RejectReason, ValueDomain } from './types';
import type { PendingEdit } from './config-values';
import { displayValue, hasOverride, isCustom, isInvalidPending } from './config-values';

// Human-readable label for each 422 reject reason; the detail (when present)
// carries the specifics ("must be between 3 and 16").
const REASON_LABEL: Record<RejectReason, string> = {
  'unknown-config': 'Unknown setting',
  'feature-disabled': 'Not available yet',
  locked: 'Platform managed',
  'invalid-type': 'Invalid value',
  'out-of-range': 'Out of range',
  'invalid-option': 'Invalid option',
  'too-long': 'Too long',
  'funnel-order': 'Funnel order',
};

// ── Config row ─────────────────────────────────────────────────────────────

interface ConfigRowProps {
  entry: ConfigSurfaceEntry;
  edit: PendingEdit | undefined;
  rejected: RejectedOverride | undefined;
  disabled: boolean; // panel-wide (saving) disable
  // Renders the row indented under its parent variant row (a nested strategy
  // knob), with a subtle left rule tying it to the select above.
  nested?: boolean;
  // Extra presentation-level disable, on top of the entry's own locked/soon
  // state (used to grey the reranker variant while the enable toggle is off).
  extraDisabled?: boolean;
  // Variant option tags to hide from the select (the reranker's 'none' option,
  // which the enable toggle owns instead).
  hideVariantTags?: string[];
  onEdit: (entry: ConfigSurfaceEntry, value: unknown) => void;
  onReset: (entry: ConfigSurfaceEntry) => void;
  onUnstage: (entry: ConfigSurfaceEntry) => void;
}

export function ConfigRow({
  entry,
  edit,
  rejected,
  disabled,
  nested,
  extraDisabled,
  hideVariantTags,
  onEdit,
  onReset,
  onUnstage,
}: ConfigRowProps) {
  const value = displayValue(entry, edit);
  const locked = entry.locked;
  const comingSoon = !entry.enabled;
  const controlDisabled = disabled || locked || comingSoon || Boolean(extraDisabled);
  const invalid = edit ? isInvalidPending(edit) : false;
  const isResetStaged = edit?.kind === 'reset';
  const custom = isCustom(entry, edit);
  const showResetLink = hasOverride(entry) && !controlDisabled;

  return (
    <div
      className={`cfg-row${comingSoon ? ' cfg-row-soon' : ''}${nested ? ' cfg-row-nested' : ''}`}
    >
      <div className="cfg-row-info">
        <div className="cfg-row-label-line">
          <span className="cfg-row-label">{entry.label}</span>
          {custom && <span className="cfg-chip cfg-chip-custom">custom</span>}
          {entry.changeCost === 'rebuild' && (
            <span className="cfg-chip cfg-chip-rebuild" title="Saving re-processes documents">
              Re-processes documents
            </span>
          )}
          {locked && <span className="cfg-chip cfg-chip-locked">Platform managed</span>}
          {comingSoon && <span className="cfg-chip cfg-chip-soon">Coming soon</span>}
        </div>
        <span className="cfg-row-help">{entry.help}</span>
        {rejected && (
          <span className="cfg-row-reject">
            {REASON_LABEL[rejected.reason]}
            {rejected.detail ? ` — ${rejected.detail}` : ''}
          </span>
        )}
      </div>

      <div className="cfg-row-control">
        <ControlField
          entry={entry}
          value={value}
          disabled={controlDisabled}
          invalid={invalid}
          hideVariantTags={hideVariantTags}
          onEdit={onEdit}
        />
        <div className="cfg-row-control-meta">
          {isResetStaged ? (
            <span className="cfg-default-note">
              <em>default</em>
              <button type="button" className="cfg-link" onClick={() => onUnstage(entry)}>
                Undo
              </button>
            </span>
          ) : (
            showResetLink && (
              <button type="button" className="cfg-link" onClick={() => onReset(entry)}>
                Reset to default
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reranking enable toggle ─────────────────────────────────────────────────
// Presentation-only row at the top of the Reranking section. It's sugar over the
// postRetrieval.reranker variant: ON means the reranker value is anything but
// 'none'. Toggling stages a value for that same id (handled by the parent) — no
// new config id is ever introduced.

interface RerankToggleRowProps {
  on: boolean;
  disabled: boolean;
  onToggle: (nextOn: boolean) => void;
}

export function RerankToggleRow({ on, disabled, onToggle }: RerankToggleRowProps) {
  return (
    <div className="cfg-row">
      <div className="cfg-row-info">
        <div className="cfg-row-label-line">
          <span className="cfg-row-label">Reranking enabled</span>
        </div>
        <span className="cfg-row-help">
          Turn reranking on to reorder fused passages before answering, or off to
          keep fused order. Pick the reranker below.
        </span>
      </div>
      <div className="cfg-row-control">
        <input
          className="cfg-toggle"
          type="checkbox"
          checked={on}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </div>
    </div>
  );
}

// ── Control field ───────────────────────────────────────────────────────────

interface ControlFieldProps {
  entry: ConfigSurfaceEntry;
  value: unknown;
  disabled: boolean;
  invalid: boolean;
  // Variant option tags to omit from the select (e.g. the reranker 'none' option
  // the enable toggle owns). Presentation only — the value shape is unchanged.
  hideVariantTags?: string[];
  onEdit: (entry: ConfigSurfaceEntry, value: unknown) => void;
}

function ControlField({ entry, value, disabled, invalid, hideVariantTags, onEdit }: ControlFieldProps) {
  // Variant: a tagged choice of code path. Options flagged enabled:false are
  // shown but unselectable, with a "(not available yet)" suffix.
  if (entry.kind === 'variant') {
    const hidden = hideVariantTags ? new Set(hideVariantTags) : null;
    const options = (entry.options ?? []).filter((opt) => !hidden?.has(opt.tag));
    return (
      <select
        className="cfg-input cfg-select"
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(e) => onEdit(entry, e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.tag} value={opt.tag} disabled={opt.enabled === false}>
            {opt.label}
            {opt.enabled === false ? ' (not available yet)' : ''}
          </option>
        ))}
      </select>
    );
  }

  const domain = entry.domain;
  if (!domain) return null;

  switch (domain.type) {
    case 'number': {
      if (domain.slider) {
        return (
          <SliderField entry={entry} domain={domain} value={value} disabled={disabled} onEdit={onEdit} />
        );
      }
      const hint = `${domain.min}–${domain.max}`;
      return (
        <span className="cfg-number-wrap">
          <input
            className={`cfg-input cfg-input-number${invalid ? ' cfg-input-invalid' : ''}`}
            type="number"
            value={typeof value === 'number' && !Number.isNaN(value) ? value : ''}
            min={domain.min}
            max={domain.max}
            step={domain.step}
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                onEdit(entry, null); // empty → locally invalid
                return;
              }
              const parsed = Number(raw);
              onEdit(entry, Number.isNaN(parsed) ? null : parsed);
            }}
          />
          <span className="cfg-range-hint">{hint}</span>
        </span>
      );
    }
    case 'boolean':
      return (
        <input
          className="cfg-toggle"
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(e) => onEdit(entry, e.target.checked)}
        />
      );
    case 'enum': {
      const optionLabel = (opt: string) => domain.optionLabels?.[opt] ?? opt;
      // Segmented button group for small enums (e.g. answer detail Brief/Balanced/
      // Detailed): the same value shape as the select, just a friendlier control.
      if (domain.segmented) {
        return (
          <div className="cfg-segmented" role="radiogroup">
            {domain.options.map((opt) => (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={value === opt}
                className={`cfg-segmented-option${
                  value === opt ? ' cfg-segmented-option-active' : ''
                }`}
                disabled={disabled}
                onClick={() => onEdit(entry, opt)}
              >
                {optionLabel(opt)}
              </button>
            ))}
          </div>
        );
      }
      return (
        <select
          className="cfg-input cfg-select"
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onEdit(entry, e.target.value)}
        >
          {domain.options.map((opt) => (
            <option key={opt} value={opt}>
              {optionLabel(opt)}
            </option>
          ))}
        </select>
      );
    }
    case 'string':
      return (
        <input
          className="cfg-input"
          type="text"
          value={typeof value === 'string' ? value : ''}
          maxLength={domain.maxLength}
          disabled={disabled}
          onChange={(e) => onEdit(entry, e.target.value)}
        />
      );
    case 'stringList':
      // No live def uses this yet — a minimal one-item-per-line textarea keeps
      // the panel from crashing when a future def arrives.
      return (
        <textarea
          className="cfg-input cfg-input-textarea"
          rows={3}
          value={Array.isArray(value) ? value.join('\n') : ''}
          disabled={disabled}
          onChange={(e) =>
            onEdit(
              entry,
              e.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line.length > 0),
            )
          }
        />
      );
    default:
      return null;
  }
}

// ── Slider field ────────────────────────────────────────────────────────────
// Range control for number domains flagged `slider`. A slider can only produce
// in-range values, so it has no invalid state. Two readout styles:
//   'balance' (default) — captioned ends + a two-sided split (the value is the
//     max-end share; the min end gets the complement). Used for Keyword↔Meaning.
//   'scale' — captioned ends + a qualitative band (min / mid / max) and NO raw
//     number, so a non-technical admin reads the effect, not the decimal.
// Without sliderLabels a plain slider shows the bare value.

interface SliderFieldProps {
  entry: ConfigSurfaceEntry;
  domain: Extract<ValueDomain, { type: 'number' }>;
  value: unknown;
  disabled: boolean;
  onEdit: (entry: ConfigSurfaceEntry, value: unknown) => void;
}

function SliderField({ entry, domain, value, disabled, onEdit }: SliderFieldProps) {
  const fallback = typeof entry.default === 'number' ? entry.default : domain.min;
  const numeric = typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
  const labels = domain.sliderLabels;
  const style = domain.sliderStyle ?? 'balance';
  const isScale = style === 'scale' && Boolean(labels);
  const format = (n: number) => (Math.round(n * 100) / 100).toFixed(2);

  let readout: string;
  if (isScale && labels) {
    // Map the position into thirds → the nearest qualitative caption.
    const span = domain.max - domain.min;
    const frac = span === 0 ? 0 : (numeric - domain.min) / span;
    readout = frac < 1 / 3 ? labels.min : frac > 2 / 3 ? labels.max : labels.mid ?? 'Medium';
  } else if (labels) {
    readout = `${labels.min} ${format(domain.max + domain.min - numeric)} · ${labels.max} ${format(numeric)}`;
  } else {
    readout = format(numeric);
  }

  return (
    <span className="cfg-slider-wrap">
      <span className="cfg-slider-row">
        {labels && <span className="cfg-slider-end">{labels.min}</span>}
        <input
          className="cfg-slider"
          type="range"
          min={domain.min}
          max={domain.max}
          step={domain.step}
          value={numeric}
          disabled={disabled}
          onChange={(e) => onEdit(entry, Number(e.target.value))}
        />
        {labels && <span className="cfg-slider-end">{labels.max}</span>}
      </span>
      <span className={isScale ? 'cfg-slider-band' : 'cfg-slider-value'}>{readout}</span>
    </span>
  );
}
